import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './Input.module.css';
import { cx } from '../../lib/cx';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  mono?: boolean;
  compact?: boolean;
  required?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, mono, compact, required, id, className, type, ...rest },
  ref,
) {
  const autoId = useId();
  // Pole s heslem má tlačítko 👁 pro odkrytí/skrytí napsaného hesla.
  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);
  const fieldId = id ?? autoId;
  const invalid = !!error;

  return (
    <div className={styles.field}>
      {label && (
        <label htmlFor={fieldId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      <div className={styles.inputWrap}>
        <input
          ref={ref}
          id={fieldId}
          type={isPassword && revealed ? 'text' : type}
          aria-invalid={invalid || undefined}
          aria-required={required || undefined}
          className={cx(
            styles.input,
            mono && styles.mono,
            compact && styles.compact,
            invalid && styles.invalid,
            isPassword && styles.withToggle,
            className,
          )}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            className={styles.revealBtn}
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? 'Skrýt heslo' : 'Zobrazit heslo'}
            aria-pressed={revealed}
            title={revealed ? 'Skrýt heslo' : 'Zobrazit heslo'}
            tabIndex={-1}
          >
            {revealed ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        )}
      </div>
      {error ? <div className={styles.error}>{error}</div> : hint ? <div className={styles.hint}>{hint}</div> : null}
    </div>
  );
});
